// Sugalarm — runtime "is this a phone-shaped device?" detection (cycle 39).
//
// Single owner of the UA / pointer / maxTouchPoints heuristic. Every
// caller that needs to know whether the SPA is running on a phone-like
// surface — regardless of viewport size — imports from here so the
// detection lives in one place. Same single-owner rule as
// `lib/capacitor.ts` for `window.Capacitor` access and
// `hooks/useFrameVariant.ts` for `matchMedia` + the kiosk flag.
//
// Used by:
//   - `useFrameVariant.compute()` — to auto-clear a stale kiosk flag
//     when the runtime is clearly a phone, and to flip the resolution
//     to "mobile" for phones in landscape (where the cycle-25 viewport
//     ≤ 600px clause would otherwise miss).
//   - `KioskPairingScreen` — to skip the cold-mount `setKiosk(true)`
//     when a phone happens to land on `/pair`.

import {
  capacitorPlatform,
  isCapacitorNative,
  isNativeKioskShell,
} from "./capacitor";

const IOS_UA = /iPhone|iPad|iPod/i;
const ANDROID_UA = /Android/i;
const ANDROID_MOBILE_UA = /Mobile/i;
const COARSE_POINTER_QUERY = "(pointer: coarse)";

const IPAD_UA = /iPad/i;
const MAC_UA = /Macintosh/i;
// An iPad's shorter logical side is ≥ 768 CSS px (iPad mini). Every
// iPhone — even the Pro Max — has a shorter side ≈ 430 px. 700 is a
// comfortable floor between the two with margin for browser chrome.
const IPAD_MIN_SHORT_SIDE = 700;
// An 11" Android tablet's shorter logical side is ≥ 700 CSS px; every
// Android phone (even in landscape) has a shorter side ≈ 360–430 px.
// Reuse the same floor the iPad path uses — orientation-independent.
const TABLET_MIN_SHORT_SIDE = 700;

/** True when the SPA is running on an iPad-class device (a large iOS
 *  tablet) — as opposed to an iPhone or a desktop. iPadOS 13+ Safari
 *  masquerades with a desktop "Macintosh" user-agent, so the
 *  touch-point count disambiguates a real Mac (≤ 1, no touchscreen)
 *  from an iPad (> 1). The shorter-viewport-side floor keeps every
 *  iPhone out without sniffing model names, and the Capacitor iOS
 *  WKWebView reports an "iPad" UA on iPad so no platform branch is
 *  needed. Pure, no side effects; `false` in any non-window context. */
export function isIPadClassRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  if (!nav) return false;
  const ua = nav.userAgent ?? "";
  const touch = nav.maxTouchPoints ?? 0;
  const iOSTablet = IPAD_UA.test(ua) || (MAC_UA.test(ua) && touch > 1);
  if (!iOSTablet) return false;
  return (
    Math.min(window.innerWidth, window.innerHeight) >= IPAD_MIN_SHORT_SIDE
  );
}

/** True when the SPA is running on an Android tablet-class device (an
 *  11"-class tablet such as the Samsung Galaxy Tab A9+/A11+) — as
 *  opposed to an Android phone or a small 8"-class tablet. The sibling
 *  of `isIPadClassRuntime()` for the Android family, and the single
 *  owner of the Android-tablet heuristic.
 *
 *  Returns `true` when the shorter viewport side is ≥ 700 CSS px
 *  (orientation-independent floor, same as the iPad path — keeps every
 *  Android phone and small tablet out) AND the runtime is Android,
 *  where "is Android" is satisfied by EITHER:
 *    - the UA matches `/Android/i` and does NOT match `/Mobile/i`
 *      (Android *phones* carry the `Mobile` token; *tablets* omit it), OR
 *    - `capacitorPlatform() === "android"` — robustly catches the
 *      Capacitor Android app build without depending on the WebView UA's
 *      `Mobile` token; the ≥ 700 px floor still excludes Android phones
 *      running the app (a phone's shorter side is ≈ 360–430 px).
 *
 *  Pure, no side effects; `false` in any non-window context. */
export function isAndroidTabletClassRuntime(): boolean {
  if (typeof window === "undefined") return false;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const ua = nav?.userAgent ?? "";
  const isAndroid =
    (ANDROID_UA.test(ua) && !ANDROID_MOBILE_UA.test(ua)) ||
    capacitorPlatform() === "android";
  if (!isAndroid) return false;
  // Cycle 326: the native Android KIOSK shell (the device-owner kiosk APK,
  // detected by its kiosk-flavor-only plugins) is ALWAYS a tablet — the
  // kiosk flavor is only ever installed on wall/desk tablets, never a phone
  // (the phone app is the `standard` flavor, which registers none of those
  // plugins). So it qualifies as tablet-class regardless of the CSS
  // shorter-side. This lets a SMALLER kiosk tablet (the 601px-CSS A11 /
  // SM-X133) render the full Android-tablet-kiosk UI (9-tile Settings, glass
  // Log, native camera + OS number pad) at its NATIVE density — no `wm
  // density` override needed. Variant resolution is unaffected:
  // `useFrameVariant.compute()` already returns "kiosk" from its step-0
  // `isNativeKioskShell()` check BEFORE it ever calls this. Non-kiosk
  // Android (the `standard` phone app, a browser) skips this branch and
  // keeps the ≥700px shorter-side floor below, so a phone is never
  // misclassified as a tablet.
  if (isNativeKioskShell()) return true;
  return (
    Math.min(window.innerWidth, window.innerHeight) >= TABLET_MIN_SHORT_SIDE
  );
}

// Cycle 199: the kiosk design canvas is a fixed pixel box scaled by one
// uniform transform. The Pi panel is 1024×600; the Samsung Galaxy Tab
// A9+/A11+ panel is 16:10 (1920×1200), so a 600-tall design box
// contain-scales with ~25px letterbox bars top/bottom. A 1024×640 box is
// exactly 16:10, so on the Android tablet width and height bind to the
// same scale → zero letterbox. This is the SINGLE SOURCE OF TRUTH for the
// design-box height, consumed by BOTH the scale denominator
// (`useKioskStageScale`) and the `.kiosk` element's CSS height (set as an
// inline custom property) so the two can never drift.
export const KIOSK_STAGE_HEIGHT_DEFAULT = 600;
export const KIOSK_STAGE_HEIGHT_TABLET = 640;

// Cycle 325: the kiosk design-box WIDTH floor (px). This was the fixed
// pre-325 design width; it is now the MINIMUM of the fluid, widen-only
// canvas width (`KioskFrame.computeKioskStage`). A device at-or-narrower
// than the 1024×designHeight aspect (the Pi 1024×600, the iPad 4:3, the
// 16:10 A11+/A9+ tablets) clamps to exactly this → byte-identical to
// pre-325; only a WIDER device (the non-16:10 A11) grows past it to fill
// the screen with no letterbox.
export const KIOSK_STAGE_WIDTH_DEFAULT = 1024;

/** The kiosk design-box height for the current runtime: 640 on an
 *  Android-tablet-class device (16:10), 600 everywhere else (the Pi
 *  panel + the iPad, which keep the cycle-53 contain-scale + letterbox).
 *  Pure; safe to call repeatedly. */
export function kioskStageHeight(): number {
  return isAndroidTabletClassRuntime()
    ? KIOSK_STAGE_HEIGHT_TABLET
    : KIOSK_STAGE_HEIGHT_DEFAULT;
}

/** True when the SPA is plausibly running on a phone or tablet
 *  regardless of viewport dimensions. Signals (any one is enough):
 *
 *  - `navigator.userAgent` matches the iOS family (iPhone / iPad / iPod)
 *    or the Android family.
 *  - `navigator.maxTouchPoints >= 1` AND the primary pointer is coarse
 *    (`(pointer: coarse)`). The two-signal AND keeps touchscreen
 *    laptops (Surface Pro etc.) from reading as phones — those report
 *    `maxTouchPoints >= 1` but their primary pointer stays `fine`
 *    because a trackpad / mouse is attached.
 *  - `isCapacitorNative()` is true — the native shell always counts as
 *    phone-like even on an iPad-class viewport.
 *
 *  Returns `false` in every SSR / non-window context. Pure function,
 *  no side effects, safe to call repeatedly. */
export function isPhoneLikeRuntime(): boolean {
  if (typeof window === "undefined") return false;
  if (isCapacitorNative()) return true;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  if (nav) {
    const ua = nav.userAgent ?? "";
    if (IOS_UA.test(ua) || ANDROID_UA.test(ua)) return true;
    const touchPoints = nav.maxTouchPoints ?? 0;
    if (touchPoints >= 1) {
      try {
        if (window.matchMedia(COARSE_POINTER_QUERY).matches) return true;
      } catch {
        /* ignore — Safari < 14 / odd embeds */
      }
    }
  }
  return false;
}
